import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Send, Loader2, Clock, FileText } from 'lucide-react';
import { useWhatsAppMessages, useWhatsAppTemplates } from '@/hooks/whatsapp';
import { TemplateSelector } from './TemplateSelector';
import type { WhatsAppContactWithDetails, TemplateMessageComponent } from '@/types/whatsapp';

interface MessageInputProps {
  businessId: string | null;
  contact: WhatsAppContactWithDetails;
}

export function MessageInput({ businessId, contact }: MessageInputProps) {
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { sendMessage, sendTemplate } = useWhatsAppMessages(businessId, contact.id);
  const { approvedTemplates } = useWhatsAppTemplates(businessId);

  const canSendFreeform = contact.is_within_window;

  const handleSend = async () => {
    if (!message.trim() || isSending) return;

    setIsSending(true);
    try {
      await sendMessage(message.trim());
      setMessage('');
      textareaRef.current?.focus();
    } finally {
      setIsSending(false);
    }
  };

  const handleTemplateSelect = async (
    templateName: string,
    components?: TemplateMessageComponent[]
  ) => {
    setShowTemplateSelector(false);
    setIsSending(true);
    try {
      await sendTemplate(templateName, components);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="border-t p-3">
      {/* 24h window warning */}
      {!canSendFreeform && (
        <Alert className="mb-3">
          <Clock className="h-4 w-4" />
          <AlertDescription>
            This conversation is outside the 24-hour messaging window.
            You must use a template message to continue the conversation.
          </AlertDescription>
        </Alert>
      )}

      {/* Template selector modal */}
      {showTemplateSelector && (
        <TemplateSelector
          templates={approvedTemplates}
          onSelect={handleTemplateSelect}
          onClose={() => setShowTemplateSelector(false)}
        />
      )}

      <div className="flex gap-2">
        {/* Template button */}
        <Button
          variant="outline"
          size="icon"
          onClick={() => setShowTemplateSelector(true)}
          title="Send template message"
        >
          <FileText className="h-4 w-4" />
        </Button>

        {canSendFreeform ? (
          <>
            {/* Free-form text input */}
            <Textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              className="min-h-[42px] max-h-32 resize-none"
              rows={1}
              disabled={isSending}
            />
            <Button
              onClick={handleSend}
              disabled={!message.trim() || isSending}
              size="icon"
            >
              {isSending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </>
        ) : (
          // Template-only mode
          <Button
            onClick={() => setShowTemplateSelector(true)}
            className="flex-1"
            disabled={isSending || approvedTemplates.length === 0}
          >
            {isSending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <FileText className="mr-2 h-4 w-4" />
            )}
            {approvedTemplates.length === 0
              ? 'No templates available'
              : 'Select a template to send'
            }
          </Button>
        )}
      </div>

      {/* Helper text */}
      {canSendFreeform && (
        <p className="mt-1 text-xs text-muted-foreground">
          Press Enter to send, Shift+Enter for new line
        </p>
      )}
    </div>
  );
}
